/**
 * Quick mode: one screen, no scrolling, nothing smaller than a thumb.
 *
 * BUILD_PLAN 5.4. The layout is a fixed-height grid rather than a document,
 * because a scorekeeper who has to scroll during a possession stops using the
 * app. Everything that changes value carries tabular numerals so the ledger
 * does not jump.
 *
 * Both tap orders work, which is not a nicety: under pressure people do it both
 * ways, and an app that only accepts one of them trains a hesitation into the
 * person holding it.
 */

import { playerLabel, spokenPlayerLabel } from './player-label.js';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import type { BoxScoreLocationState } from './BoxScoreLink.js';
import {
  Dialog,
  diagnosticFor,
  errorCopyFor,
  useDialog,
  useToast,
  supportCodeLine,
} from '@good-season/ui';
import { navigation } from '@good-season/platform';

import { ApiError, OfflineError } from '../api/client.js';
import {
  OutboxSupersededError,
  OutboxUnavailableError,
  OutboxUncertainError,
  UnrecordableEvent,
  type FinalScore,
  type LoggedEvent,
} from './db.js';
import {
  FOUL_KINDS,
  NOTE_MAX_CHARS,
  NOTE_META_KEY,
  bonusStanding,
  clockLabel,
  describePlay,
  finalScoreFrom,
  foulStanding,
  noteFrom,
  parseClock,
  teamFoulsIn,
  timeoutsIn,
  voidGroup,
  type TimeoutsUsed,
} from './game.js';
import { PlaysTimeline } from './PlaysTimeline.js';
import {
  type BookEvent,
  type BookFacts,
  initialBookFacts,
  lostAnswerCopy,
  mayBeFinalOf,
  mayHaveCopy,
  noticeFactsOf,
  noticeOutcomesOf,
  reduceBookFacts,
  statusCopy,
  stopShort,
  unsureNotice,
  unsureShort,
} from './bookFacts.js';
import { StopNotice } from './StopNotice.js';
import { SupersededNotice, noticeBody } from './SupersededNotice.js';
import { SyncIndicator } from './SyncIndicator.js';
import { type LostFact, UNAVAILABLE_TITLE, lostBody, winningStop } from './unavailable.js';
import { StatusNotKept, mayHaveCommitted } from './afterServer.js';
import { useFit } from './useFit.js';
import {
  PeriodMovedError,
  SELECTION_TIMEOUT_MS,
  useScorebook,
  type EventDraft,
} from './useScorebook.js';

/** How long the modifiers ignore a tap after the strip or the armed row stood where they are. */
const SLOT_SETTLE_MS = 400;

/** The scorebook's prefix on a diagnostic code, so support knows which screen. */
const SURFACE = 'SB';

interface QuickAction {
  readonly key: string;
  readonly label: string;
  readonly made: string;
  readonly missed: string;
  readonly points: number;
  /**
   * Belongs to the team, not to a child: a team rebound is recorded the moment
   * it is tapped and never arms, because the next player tapped would
   * otherwise be credited with something no player did.
   */
  readonly teamLevel?: boolean;
}

const FOUL: QuickAction = {
  key: 'FOUL',
  label: 'Foul',
  made: 'FOUL_PERSONAL',
  missed: 'FOUL_PERSONAL',
  points: 0,
};

/**
 * Full mode's second action row (BUILD_PLAN 5.4).
 *
 * Same shape as the quick actions so there is one tap handler, not two: these
 * have no made-or-missed variant, so `made` and `missed` are the same type and
 * the misses modifier leaves them alone. None of them scores.
 *
 * Rendered AFTER the primary row in the DOM, deliberately. `+2` stays the first
 * `.sb__action` on the page whichever mode is on, and the tap-latency
 * measurement in `e2e/scorebook-offline.spec.ts` reaches for it by that index.
 */
const FULL_ACTIONS: readonly QuickAction[] = [
  { key: 'REB_OFF', label: 'REB O', made: 'REB_OFF', missed: 'REB_OFF', points: 0 },
  { key: 'REB_DEF', label: 'REB D', made: 'REB_DEF', missed: 'REB_DEF', points: 0 },
  { key: 'AST', label: 'AST', made: 'AST', missed: 'AST', points: 0 },
  { key: 'STL', label: 'STL', made: 'STL', missed: 'STL', points: 0 },
  { key: 'BLK', label: 'BLK', made: 'BLK', missed: 'BLK', points: 0 },
  { key: 'TO', label: 'TO', made: 'TO', missed: 'TO', points: 0 },
  {
    key: 'REB_TEAM',
    label: 'Team REB',
    made: 'REB_TEAM',
    missed: 'REB_TEAM',
    points: 0,
    teamLevel: true,
  },
  { key: 'DEFLECTION', label: 'Deflect', made: 'DEFLECTION', missed: 'DEFLECTION', points: 0 },
  {
    key: 'CHARGE_TAKEN',
    label: 'Charge',
    made: 'CHARGE_TAKEN',
    missed: 'CHARGE_TAKEN',
    points: 0,
  },
];

/** `:focus-visible`, false where the engine cannot answer the question. */
function focusVisible(element: HTMLElement): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return false;
  }
}

/**
 * Words for the timeouts a side has taken this period, shown beside its score.
 *
 * "Taken", not "used": the e2e specs find a side's score by the text `Us`
 * inside it, case-insensitively, and "used" contains it.
 */
function timeoutsLine(used: TimeoutsUsed, detail = false): string {
  const total = used.full + used.short;
  const base = `${String(total)} timeout${total === 1 ? '' : 's'} taken`;
  // The short count is for the spoken line and the sheet. On the header it
  // would wrap to a third line and grow the header mid-game, which moves every
  // control below it.
  return detail && used.short > 0 ? `${base}, ${String(used.short)} short` : base;
}

/**
 * The armed half of a two-tap choice.
 *
 * `bench` is separate from `player` because the two mean different things when
 * the next tap lands: a bench player armed and an on-court player tapped is a
 * substitution, while an on-court player armed and an action tapped is a play.
 * A bench player armed and an action tapped is NOT a play for the bench
 * player: somebody on the bench cannot score, and a forgotten arm would credit
 * the next basket to a child sitting down. The action arms instead.
 */
type Selection =
  | { readonly kind: 'player'; readonly playerId: string }
  | { readonly kind: 'bench'; readonly playerId: string }
  | { readonly kind: 'action'; readonly action: QuickAction }
  | null;

export function ScorebookScreen() {
  const { gameId = '' } = useParams();
  const { search } = useLocation();
  const book = useScorebook(gameId);
  const dialog = useDialog();
  const { toast } = useToast();
  const [selection, setSelection] = useState<Selection>(null);
  const [missing, setMissing] = useState(false);
  const [them, setThem] = useState(false);
  const [full, setFull] = useState(false);
  const [timeline, setTimeline] = useState(false);
  const [more, setMore] = useState(false);
  const [noticesOpen, setNoticesOpen] = useState(false);
  /** What waits for the More sheet to close and hand focus back. */
  const afterMore = useRef<(() => void) | null>(null);
  /** An End period is being written (`endPeriod`). */
  const ending = useRef(false);
  /*
   * A control that changes meaning under the finger is a mistake waiting for a
   * double tap: the armed row and the foul strip stand where the three
   * modifiers do, so a second tap meant for "No player" or "Cancel" or a kind
   * landed on "For us", or on Quick/Full, the moment the row went. For this
   * long after a TAP takes one away (No player, Cancel, Escape), the modifiers
   * ignore a tap. Not after the strip's own four second fuse: nobody tapped to
   * end that, so there is no second tap in flight, and a scorekeeper moving on
   * to the next play must not be held up.
   */
  const slotSettledAt = useRef(0);
  const holdModifiers = () => {
    slotSettledAt.current = Date.now();
  };
  /** The foul just recorded, whose kind can be changed for a few seconds. */
  const [foulId, setFoulId] = useState<string | null>(null);
  const foulTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stripRef = useRef<HTMLElement | null>(null);
  /** The foul was recorded from the keyboard, so focus follows it into the strip. */
  const focusStrip = useRef(false);
  /** Finish is waiting on the outbox to empty. A designed state, not a spinner. */
  const [waiting, setWaiting] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const asking = useRef(false);
  /** The refusal that said a newer app version took the database, or null. */
  const [superseded, setSuperseded] = useState<OutboxSupersededError | null>(null);
  /**
   * The refusal that said the store was wiped, or null. Held so the persistent
   * notice shows the moment a tap finds the wipe, without waiting for a sync
   * tick, which can stall behind an in-flight POST.
   */
  const [unavailable, setUnavailable] = useState<OutboxUnavailableError | null>(null);
  /**
   * Everything the notices must say that this page learned along the way: the
   * one record of the server's game status, whether this phone may disagree
   * with it, whether an entry may be missing, and whether the book on screen
   * may be behind disk. Every notice and the persistent unsure notice are
   * DERIVED from it (`bookFacts.ts`), so no two holders can disagree. A ref
   * mirrors it so a handler can build a dialog from the state it just made.
   */
  const factsRef = useRef<BookFacts>(initialBookFacts);
  const [facts, setFacts] = useState<BookFacts>(initialBookFacts);
  const stopSignature = [
    superseded !== null,
    unavailable !== null,
    // Only a stop in the sync status. The status cycles every few seconds on a
    // bad network, and that must not restart the fit.
    book.status.kind === 'superseded' || book.status.kind === 'unavailable' ? book.status.kind : '',
    facts.entry,
    facts.server,
    facts.behind,
    facts.stale?.diagnostic ?? '',
  ].join(',');
  /*
   * What the book can show, found by measuring (`useFit`): more compact one
   * level at a time until what is above the tray fits. The signature is what
   * restarts that from the top: the things that change how tall the book is.
   */
  const fit = useFit(
    [
      book.log.length,
      book.finished,
      book.persistence,
      full,
      foulId === null,
      selection?.kind ?? '',
      waiting,
      // A stop or an unsure result arriving, or saying something else, is a
      // change in what the book contains: the notice's height is different.
      stopSignature,
    ].join('|'),
  );
  /** Full mode's extra actions are in the More sheet, not on the page. */
  const short = fit.level >= 1;
  /** The sync line is one line. */
  const compact = fit.level >= 2;
  /** Banners and the badge are one line. */
  const oneLine = fit.level >= 3;
  const note = useCallback((event: BookEvent): BookFacts => {
    factsRef.current = reduceBookFacts(factsRef.current, event);
    setFacts(factsRef.current);
    return factsRef.current;
  }, []);
  const reread = book.reread;
  /**
   * After a write whose outcome is unknown, read the book from disk again so
   * the timeline and score show what landed. Resolves to null when they now
   * do, to a stop if the read heard one (handled by the caller as that stop),
   * and otherwise puts up the persistent notice and resolves to 'notice'.
   */
  const showWhatLanded = useCallback(
    async (entry: boolean, diagnostic: string): Promise<unknown> => {
      try {
        await reread();
        note({ type: 'reread', ok: true });
        return null;
      } catch (failure) {
        if (failure instanceof OutboxSupersededError || failure instanceof OutboxUnavailableError) {
          return failure;
        }
        // The book on screen may now be behind disk, so whatever stop later
        // outranks the unsure notice must not say every play is saved. A Finish
        // or Reopen has already noted its status, whatever the re-read finds.
        if (entry) note({ type: 'entry' });
        note({ type: 'reread', ok: false, diagnostic });
        return 'notice';
      }
    },
    [note, reread],
  );

  /**
   * Arming a player or an action starts a four second fuse (BUILD_PLAN 5.4).
   *
   * Without it a scorekeeper who taps a player and then gets distracted by the
   * game attributes the next play, whenever it comes, to whoever they happened
   * to touch two minutes ago.
   *
   * A BENCH selection has no fuse. A substitution happens at a dead ball,
   * BUILD_PLAN 5.5 asks for none, and the fuse was the thing that made the swap
   * unusable with a screen reader: reading three court cards to pick the one
   * going off takes longer than four seconds, and when the fuse fired it
   * unmounted the open-spot button under a keyboard user's focus. A bench arm
   * stays until it is used or tapped again. Found by the fresh-context pass.
   */
  const arm = useCallback((next: Selection) => {
    setSelection(next);
    if (timer.current !== null) clearTimeout(timer.current);
    if (next !== null && next.kind !== 'bench') {
      timer.current = setTimeout(() => setSelection(null), SELECTION_TIMEOUT_MS);
    }
  }, []);

  /**
   * The foul-kind strip lives as long as a selection does (four seconds).
   * KEYBOARD focus inside it (`:focus-visible`) holds it open and leaving
   * starts the fuse again; pointer and touch focus never hold it. Focus that
   * moves with no key (a screen reader, Switch Access, TalkBack) is not held
   * either, so the strip is the quick path only: the plays timeline's "Change
   * kind" is the untimed one.
   */
  const stopFoulFuse = useCallback(() => {
    if (foulTimer.current !== null) clearTimeout(foulTimer.current);
    foulTimer.current = null;
  }, []);
  const startFoulFuse = useCallback(() => {
    stopFoulFuse();
    foulTimer.current = setTimeout(() => setFoulId(null), SELECTION_TIMEOUT_MS);
  }, [stopFoulFuse]);

  useEffect(() => {
    if (foulId === null || !focusStrip.current) return;
    focusStrip.current = false;
    stripRef.current?.querySelector<HTMLElement>('[aria-pressed="true"]')?.focus();
  }, [foulId]);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
      if (foulTimer.current !== null) clearTimeout(foulTimer.current);
    },
    [],
  );

  /**
   * A write that did not happen, said out loud.
   *
   * Every path that writes to the log goes through this: a play, a swap, an
   * undo, a void, an attribution. An event this device refused to queue
   * (because the server would refuse it forever), or a store that would not
   * take the write, is never silent: a tap that recorded nothing while
   * looking like it worked is the failure this whole branch exists to
   * prevent. The first version guarded one of the five paths and promised all
   * of them; the fresh-context pass counted.
   */
  const refused = useCallback(
    async (thrown: unknown, tapRefused = true) => {
      if (thrown instanceof PeriodMovedError) {
        // Nothing was written and nothing is wrong with the store: the tap was
        // made in a period the book had already left (ADR-0110).
        toast({
          title: 'That tap was not recorded.',
          body: `Period ${String(thrown.shown)} had already ended when it was saved, so it was not put in period ${String(thrown.current)}. The book now shows period ${String(thrown.current)}. Tap it again if it belongs there.`,
        });
        return;
      }
      let error = thrown;
      if (error instanceof OutboxUncertainError) {
        // The first attempt may have landed, and the screen only shows a write
        // that resolved, so the book on screen can be behind disk. Read it
        // again before saying anything: telling the coach to check a stale
        // timeline is what double counts the play (delta review of ed60469).
        const diagnostic = diagnosticFor(error, SURFACE);
        const after = await showWhatLanded(true, diagnostic);
        if (after === 'notice') return;
        if (after === null) {
          await dialog.alert({ ...uncertainCopy(mayBeFinalOf(factsRef.current)), diagnostic });
          return;
        }
        // The re-read heard a stop, which is what the coach must be told.
        error = after;
      }
      // Not a dialog: a dismissed dialog leaves the next tap refused with
      // nothing on screen. The notice stays until the page reloads. The play
      // that was refused may or may not be on disk (a retried write can have
      // committed before its acknowledgement was lost), so the copy never says
      // it is absent. The
      // notice keeps every outcome it has been given; this only adds one.
      if (error instanceof OutboxSupersededError) {
        // A read for the Finish gate refuses no tap, so it claims no lost play
        // and leaves whatever an earlier refusal already said.
        // The wiped notice may still replace this one (db.ts moves a stop on
        // when it finds the store gone), so it needs the same fact.
        if (tapRefused) note({ type: 'entry' });
        setSuperseded(error);
        return;
      }
      if (error instanceof OutboxUnavailableError) {
        if (tapRefused) note({ type: 'entry' });
        setUnavailable(error);
      }
      const copy =
        error instanceof UnrecordableEvent
          ? {
              title: 'That play could not be recorded.',
              body: 'Nothing was lost. Record it again, or add it from the timeline later.',
            }
          : error instanceof OutboxUnavailableError
            ? unavailableCopy(noticeFactsOf(factsRef.current), mayBeFinalOf(factsRef.current))
            : errorCopyFor();
      // A code even for a failure that never reached a server: derived from
      // the error, so support can tell two different refusals apart.
      await dialog.alert({
        title: copy.title,
        body: copy.body,
        diagnostic: diagnosticFor(error, SURFACE),
      });
    },
    [dialog, note, showWhatLanded, toast],
  );

  const commit = useCallback(
    async (...drafts: readonly EventDraft[]): Promise<readonly LoggedEvent[]> => {
      arm(null);
      // The next write ends the previous foul's window to be re-typed.
      stopFoulFuse();
      setFoulId(null);
      return book.record(...drafts).catch(async (error: unknown) => {
        await refused(error);
        return [];
      });
    },
    [arm, book, refused, stopFoulFuse],
  );

  const play = useCallback(
    (action: QuickAction, playerId: string | null) => {
      const type = missing ? action.missed : action.made;
      const scores = !missing && action.points > 0;
      /*
       * Whether a keyboard pressed the control that is focused now. Only then
       * does focus move into the strip: for a thumb it would pause the strip's
       * fuse for good, and the strip would never leave.
       */
      const active = document.activeElement;
      const fromKeyboard = active instanceof HTMLElement && focusVisible(active);
      void commit({
        type,
        team: them ? 'them' : 'us',
        // An opponent play is never attributed to this roster.
        playerId: them ? null : playerId,
        value: scores ? action.points : null,
      }).then((rows) => {
        // A foul is recorded as a personal foul in this one tap. The strip is
        // the extra tap that makes it another kind.
        const recorded = rows[0];
        if (action.key === FOUL.key && recorded !== undefined) {
          focusStrip.current = fromKeyboard;
          setFoulId(recorded.id);
          startFoulFuse();
        }
      });
    },
    [commit, missing, startFoulFuse, them],
  );

  /**
   * Put a finished game back on the floor so the held plays can land.
   *
   * Confirmed rather than immediate: reopening is a decision about a game
   * somebody has already closed, and the coach on the other end of it may have
   * sent the final score out already.
   */
  async function reopenGame() {
    const yes = await dialog.confirm({
      title: 'Reopen this game?',
      body: 'It was marked finished. Reopening lets the plays on this phone finish sending.',
      confirmLabel: 'Reopen',
    });
    if (!yes) return;

    try {
      await book.reopen();
      // The server and this phone agree on the status now.
      note({ type: 'status', act: 'reopen', result: 'plain' });
    } catch (error) {
      await sayWhatWentWrong(error, 'reopened');
    }
  }

  /**
   * Any failure that came back from the server or the network, in this app's
   * words.
   *
   * `error.code`, never `error.message`. The server's prose is for a log, and
   * the diagnostic is carried through so a coach can read six characters down
   * the phone to somebody who can find the entry it names.
   */
  async function sayWhatWentWrong(thrown: unknown, ifSuperseded: 'finished' | 'reopened') {
    let error = thrown;
    const act = ifSuperseded === 'reopened' ? 'reopen' : 'finish';
    if (error instanceof StatusNotKept || error instanceof OutboxUncertainError) {
      // Finish and Reopen reach here only after the server call succeeded, so
      // what is known is the server's side; only this phone's copy is unsure.
      // Read the book again so the screen shows whichever status this phone
      // actually holds, and say what to do if it is the old one.
      note({ type: 'status', act, result: 'notKept' });
      const diagnostic = diagnosticFor(
        error instanceof StatusNotKept ? error.cause : error,
        SURFACE,
      );
      const after = await showWhatLanded(false, diagnostic);
      if (after === 'notice') return;
      if (after === null) {
        await dialog.alert({ ...statusCopy(ifSuperseded, false), diagnostic });
        return;
      }
      error = after;
    }
    // Never the "did not go through, try again" copy: a superseded store
    // refuses every retry until the page reloads, and Finish and Reopen reach
    // here AFTER the server call has succeeded, when only the local cache
    // write was refused. The notice says which call that was. Nothing here
    // reconciles the cached status with the server's after the reload.
    if (error instanceof OutboxSupersededError) {
      note({ type: 'status', act, result: 'notKept' });
      setSuperseded(error);
      return;
    }
    // `diagnosticFor` carries an ApiError's own code through and derives one
    // for everything else, so every dialog here has something to read out.
    const diagnostic = diagnosticFor(error, SURFACE);
    if (error instanceof OutboxUnavailableError) {
      // Finish and Reopen reach here only after the server call succeeded.
      note({ type: 'status', act, result: 'notKept' });
      setUnavailable(error);
      await dialog.alert({
        ...unavailableCopy(noticeFactsOf(factsRef.current), mayBeFinalOf(factsRef.current)),
        diagnostic,
      });
      return;
    }
    // The POST answered with a 5xx the handler could have raised after its
    // commit, or with a 200 that could not be read (`mayHaveCommitted`), so the
    // game can already be final (or open) on the server. A code the
    // middleware raises before any handler, and a 4xx, committed nothing and
    // keep their code's copy below, and change nothing in the record; the same
    // rule as Start's. The local half never ran, so this phone still holds the
    // old status; the book is read again anyway. A stop that read hears
    // replaces the "do it again" dialog (it cannot work over a stopped store),
    // and the stop's own copy carries the hedged fact, in the notice and in the
    // wipe dialog alike. Doing it again is NOT harmless once the answer is
    // unknown: another device may have changed the game since, so a repeated
    // Finish of an open game records the score typed here as its final score,
    // and a repeated Reopen opens it again over another device's Finish. The
    // copy says so (`FINISH_CAUTION`, `REOPEN_CAUTION` in `bookFacts.ts`).
    // A request that was sent and got no answer is the same story: the
    // connection can drop after the server committed. Only an OfflineError
    // raised before the request left (`neverSent`) proves nothing happened.
    const lostAnswer = error instanceof OfflineError && !error.neverSent;
    if (mayHaveCommitted(error) || lostAnswer) {
      note({ type: 'status', act, result: 'mayHave' });
      const heard: unknown = await reread().then(
        () => null,
        (failure: unknown) => failure,
      );
      if (heard instanceof OutboxSupersededError) {
        setSuperseded(heard);
        return;
      }
      if (heard instanceof OutboxUnavailableError) {
        setUnavailable(heard);
        // Built from the record as it stands now, as the notice under it is.
        await dialog.alert({
          ...unavailableCopy(noticeFactsOf(factsRef.current), mayBeFinalOf(factsRef.current)),
          diagnostic: diagnosticFor(heard, SURFACE),
        });
        return;
      }
      if (heard === null) note({ type: 'reread', ok: true });
      await dialog.alert({
        ...(lostAnswer
          ? lostAnswerCopy(ifSuperseded === 'reopened')
          : mayHaveCopy(ifSuperseded === 'reopened')),
        diagnostic,
      });
      return;
    }
    if (error instanceof OfflineError) {
      await dialog.alert({
        title: 'No signal, so that did not go through.',
        body: 'Nothing was lost. Try again once you have a bar or two.',
        diagnostic,
      });
      return;
    }
    const copy = error instanceof ApiError ? errorCopyFor(error.code) : errorCopyFor();
    await dialog.alert({ title: copy.title, body: copy.body, diagnostic });
  }

  /**
   * The final score, offered as this book counted it and confirmed by the
   * person who watched the game.
   *
   * Prefilled rather than sent unasked, because the number that lands is the
   * OFFICIAL one (BUILD_PLAN 13.8 D10): it drives the win-loss record and sits
   * beside the derived pair precisely so a disagreement between them can be
   * seen. Nothing on the server checks one against the other.
   */
  async function askScoreAndFinish() {
    const ourEntry = await dialog.prompt({
      title: 'Final score',
      body: 'This is the score that goes to the league. It is stored beside the one this book counted.',
      label: 'Us',
      numeric: true,
      defaultValue: String(book.scores.ourScore),
      confirmLabel: 'Next',
    });
    if (ourEntry === null) return;

    const theirEntry = await dialog.prompt({
      title: 'Final score',
      label: 'Them',
      numeric: true,
      defaultValue: String(book.scores.theirScore),
      // Not "Finish game", which is the tray control that opened this. Two
      // buttons with one name, both on screen, is a locator a screen reader
      // user cannot tell apart either.
      confirmLabel: 'Close the book',
    });
    if (theirEntry === null) return;

    const ourScore = finalScoreFrom(ourEntry);
    const theirScore = finalScoreFrom(theirEntry);
    if (ourScore === null || theirScore === null) {
      await dialog.alert({
        title: 'That score needs another look.',
        body: 'Each side takes a whole number from 0 to 999. Nothing has been closed.',
      });
      return;
    }

    try {
      const stored = await book.finish(ourScore, theirScore);
      // The server and this phone agree on the status now, and the game is
      // final: the record keeps that for the entry sentences.
      note({ type: 'status', act: 'finish', result: 'plain' });
      await dialog.alert(closedCopy({ us: ourScore, them: theirScore }, stored));
    } catch (error) {
      await sayWhatWentWrong(error, 'finished');
    }
  }

  /**
   * Finish, but only once every play has reached the server.
   *
   * This is the whole reason the button is not a plain POST. The moment a game
   * goes final the sync route refuses further events with `GAME_FINALIZED`, so
   * anything still in the outbox is stranded on the phone until somebody
   * reopens the game, and the coach who closed the book has no reason to think
   * there is anything left to send.
   *
   * The count is read from DISK for this game only, as the sync indicator's
   * number now is (ADR-0066), and not from React state, which is stale at
   * exactly the moment this decides.
   */
  async function finishGame() {
    if (asking.current) return;
    asking.current = true;
    try {
      let held: number;
      try {
        held = await book.unsyncedHere();
      } catch (error) {
        await refused(error, false);
        return;
      }
      if (held === 0) {
        await askScoreAndFinish();
        return;
      }

      const wait = await dialog.confirm({
        title: `${String(held)} ${held === 1 ? 'play has' : 'plays have'} not reached the server.`,
        body: 'Closing the book now would leave them on this phone. Send them first, then finish.',
        confirmLabel: 'Send them first',
        cancelLabel: 'Not yet',
      });
      // Cleared rather than left alone, so somebody who taps Finish again while
      // the banner is up and then backs out is not left watching it.
      if (!wait) {
        setWaiting(false);
        return;
      }

      setWaiting(true);
      book.retryNow();
    } finally {
      asking.current = false;
    }
  }

  /**
   * The other half of Finish: pick the flow back up once the outbox drains.
   *
   * Keyed on `book.status` rather than on a timer, so it re-checks each time
   * the sync engine reports, including the tick it fires on reconnect. The
   * count still comes from disk: `status` is what the engine last reported,
   * and disk is what the gate decides on.
   */
  useEffect(() => {
    if (!waiting) return undefined;
    let cancelled = false;

    void (async () => {
      let held: number;
      try {
        held = await book.unsyncedHere();
      } catch (error) {
        if (cancelled) return;
        setWaiting(false);
        void refused(error, false);
        return;
      }
      if (cancelled || held > 0 || asking.current) return;
      asking.current = true;
      setWaiting(false);
      try {
        await askScoreAndFinish();
      } finally {
        asking.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
    // `book` is rebuilt every render, so it cannot be a dependency without
    // re-running this on every render. The status object is what actually
    // carries news of the outbox.
  }, [waiting, book.status]);

  /**
   * Void one play from the timeline. Confirmed, because nothing un-voids it.
   *
   * Named for what the tap wrote: a swap or a period change goes back whole,
   * and the question says so rather than naming one half of it.
   */
  async function voidFromTimeline(row: LoggedEvent) {
    const group = voidGroup(book.plays, row);
    const what =
      group.length === 1
        ? describePlay(row).toLowerCase()
        : group.some((other) => other.event.type === 'PERIOD_END')
          ? 'the end of that period'
          : 'that substitution';
    const yes = await dialog.confirm({
      title: `Take back ${what}?`,
      body: 'It stays in the record as a correction. The score and the box score stop counting it.',
      confirmLabel: 'Take it back',
      tone: 'danger',
    });
    if (yes) await book.voidPlay(row).catch(refused);
  }

  /**
   * The whistle: close this period at the reading it actually went, then open
   * the next one at full. ONE tap, so one transaction: an app kill between the
   * two rows would leave a period ended and never reopened.
   *
   * `PERIOD_END` takes the live reading rather than a tidy zero, which is the
   * corollary of never clamping the clock. If a period ran long the reading is
   * negative and the plays that happened after it carry readings after it, so
   * the engine can still tell a substitution from a basket. Reordering them to
   * look neat is what destroys the attribution.
   */
  async function endPeriod() {
    // One End period at a time: a second tap while the first is writing is
    // the same whistle, not a second one.
    if (book.game === null || ending.current) return;
    ending.current = true;
    arm(null);
    stopFoulFuse();
    setFoulId(null);
    // The period this screen shows is the one the coach means. The write
    // checks it against the book on disk (`useScorebook.endPeriod`).
    const shown = book.clock.period;
    try {
      const result = await book.endPeriod(shown).catch(async (error: unknown) => {
        await refused(error);
        return null;
      });
      if (result?.kind === 'already') {
        toast({
          title: `Period ${String(shown)} already ended on this phone.`,
          body: `The book now shows period ${String(result.current)}. Tap End period again to end that one.`,
        });
      }
    } finally {
      ending.current = false;
    }
  }

  /**
   * Drift correction (BUILD_PLAN 5.6): type what the scoreboard shows. The
   * referee's clock is the authority, and the plan expects this two or three
   * times a game. A `SET_CLOCK` row is a plain tap: Undo takes it back, and
   * the clock keeps running or stays stopped, whichever it was (`clockAt`).
   */
  async function setClock() {
    const entered = await dialog.prompt({
      title: 'Set the clock',
      body: 'Type what the scoreboard shows. The clock keeps running or stays stopped, whichever it is now.',
      label: 'Clock',
      defaultValue: clockLabel(Math.max(0, book.clock.remainingMs)),
      confirmLabel: 'Set',
    });
    if (entered === null) return;

    const gameClockMs = parseClock(entered);
    if (gameClockMs === null) {
      await dialog.alert({
        title: 'That is not a clock reading.',
        body: 'Minutes and seconds, like 7:11. Nothing was changed.',
      });
      return;
    }
    await commit({ type: 'SET_CLOCK', team: 'us', gameClockMs });
  }

  /**
   * Change the foul on the strip to another kind. The strip then follows the
   * foul's new row, so a wrong second guess is one more tap, not a trip to the
   * timeline.
   */
  async function changeFoul(row: LoggedEvent, type: string) {
    const changed = await retypeWithFeedback(row, type);
    if (changed === null) return;
    setFoulId(changed.id);
    // A KEYBOARD's focus holds the strip open. A touch tap focuses the button it
    // lands on in Chromium, and holding for that left the strip, and the
    // modifiers it hides, up for good after a thumb retype. Focus that moves
    // with no key (a screen reader, Switch Access) gets only the four seconds,
    // which is why the timeline's "Change kind" exists: it has no timer.
    if (!keyboardInStrip()) startFoulFuse();
  }

  /**
   * One foul change, and everything the person must be told about how it went.
   * Resolves to the row now standing for the foul, or null when nothing changed
   * (and, but for the same kind, they have been told why).
   *
   * A refused write says only the refusal (`refused`), never also that the foul
   * "was already changed": the foul is as it was, and a second message about a
   * different cause would be a lie on top of the first.
   */
  async function retypeWithFeedback(row: LoggedEvent, type: string): Promise<LoggedEvent | null> {
    if (row.event.type === type) return null;
    let result: Awaited<ReturnType<typeof book.retype>>;
    try {
      result = await book.retype(row, type);
    } catch (error) {
      await refused(error);
      return null;
    }
    if (result.kind === 'busy') {
      toast({
        title: 'Still saving the last change.',
        body: 'Nothing was recorded for that tap. Pick the foul kind again.',
      });
      return null;
    }
    if (result.kind === 'gone') {
      const now = FOUL_KINDS.find((kind) => kind.type === result.current);
      toast({
        title:
          now === undefined
            ? 'That foul was already changed.'
            : `That foul is already recorded as ${now.label.toLowerCase()}.`,
        body: 'Pick the kind you want to change it again.',
      });
      return null;
    }
    return result.row;
  }

  function keyboardInStrip(): boolean {
    const active = document.activeElement;
    return (
      active instanceof HTMLElement &&
      stripRef.current?.contains(active) === true &&
      focusVisible(active)
    );
  }

  function closeStrip() {
    holdModifiers();
    stopFoulFuse();
    setFoulId(null);
    document.querySelector<HTMLElement>('[data-action="FOUL"]')?.focus();
  }

  /** One timeout, jump ball or note from the More sheet, which then closes. */
  async function recordFromMore(...drafts: readonly EventDraft[]) {
    setMore(false);
    await commit(...drafts);
  }

  /**
   * A coach's note, typed into the design system's prompt (never the browser's)
   * and pinned to the clock reading it was written at.
   */
  async function addNote() {
    /*
     * The More sheet hands focus back to the button that opened it when it
     * closes. A prompt opened before that happens has focus taken off it by the
     * return, so a coach typing a note, or a key meant for the note, lands on
     * More. The sheet says when it has closed and focus is back (`onClosed`); the
     * prompt waits for that. The timer is only a net for a sheet that never
     * reports (it was not open).
     */
    await new Promise<void>((resolve) => {
      afterMore.current = resolve;
      setMore(false);
      setTimeout(() => {
        if (afterMore.current === resolve) {
          afterMore.current = null;
          resolve();
        }
      }, 600);
    });
    const entered = await dialog.prompt({
      title: 'Add a note',
      body: 'Pinned to the clock for film review later. It does not change any stat.',
      label: 'Note',
      confirmLabel: 'Save note',
    });
    if (entered === null) return;

    const note = noteFrom(entered);
    if (note.kind !== 'ok') {
      await dialog.alert({
        title: note.kind === 'empty' ? 'That note is empty.' : 'That note is too long.',
        body:
          note.kind === 'empty'
            ? 'Type a few words and try again. Nothing was recorded.'
            : `A note holds up to ${String(NOTE_MAX_CHARS)} characters. Shorten it and try again. Nothing was recorded.`,
      });
      return;
    }
    await commit({ type: 'NOTE', team: 'us', meta: { [NOTE_META_KEY]: note.text } });
  }

  function tapAction(action: QuickAction) {
    if (action.teamLevel === true) {
      play(action, null);
      return;
    }
    if (selection?.kind === 'player') {
      play(action, selection.playerId);
      return;
    }
    // Tapping the armed action again commits it with nobody named. This is the
    // feature BUILD_PLAN 5.7 says keeps a scorekeeper from falling behind: an
    // unattributed play is recorded now and attributed at halftime.
    if (selection?.kind === 'action' && selection.action.key === action.key) {
      play(action, null);
      return;
    }
    arm({ kind: 'action', action });
  }

  /** One action button, shared by both rows so the two cannot drift apart. */
  function actionButton(action: QuickAction) {
    const armed = selection?.kind === 'action' && selection.action.key === action.key;
    return (
      <button
        /*
         * A STABLE accessible name. Without it the hint below joins the name,
         * so the control a screen reader was told to find is called one thing
         * before it is armed and another after, which is the moment somebody is
         * trying to press it a second time.
         */
        aria-label={action.label}
        aria-pressed={armed}
        data-action={action.key}
        className={`sb__action${armed ? ' sb__action--armed' : ''}`}
        key={action.key}
        onClick={() => tapAction(action)}
        type="button"
      >
        <span aria-hidden="true" className="sb__actionLabel">
          {action.label}
        </span>
        {armed ? (
          <span aria-hidden="true" className="sb__actionHint">
            Again for no player
          </span>
        ) : null}
      </button>
    );
  }

  function tapPlayer(playerId: string, onFloor: boolean) {
    if (selection?.kind === 'action') {
      play(selection.action, playerId);
      return;
    }
    // The armed bench player again: stand down. With no fuse on a bench arm
    // this is the way to change your mind.
    if (selection?.kind === 'bench' && selection.playerId === playerId) {
      arm(null);
      return;
    }
    // A bench player armed, then an on-court player tapped, is the swap.
    if (selection?.kind === 'bench' && onFloor) {
      const outgoing = playerId;
      const incoming = selection.playerId;
      arm(null);
      void book.substitute(outgoing, incoming).catch(refused);
      return;
    }
    arm({ kind: onFloor ? 'player' : 'bench', playerId });
  }

  /**
   * A bench player armed, then an open spot tapped: on they come, with nobody
   * going off.
   *
   * The only way the floor ever grows. Every swap is one off for one on, so a
   * game started with four because the fifth was late had no way to put the
   * fifth on when they arrived, and BUILD_PLAN 5.3's count warning named a
   * repair that did not exist. Found by the delta pass.
   */
  function fillSpot(playerId: string) {
    void commit({ type: 'SUB_IN', team: 'us', playerId });
  }

  // One precedence rule: a wipe heard by anything, whenever, beats a takeover.
  // The failure that kept the book from opening is one of the things heard, so
  // a wipe found later replaces a full-screen takeover too.
  const stop = winningStop([
    superseded !== null ||
    book.status.kind === 'superseded' ||
    book.failure instanceof OutboxSupersededError
      ? 'superseded'
      : null,
    unavailable !== null ||
    book.status.kind === 'unavailable' ||
    book.failure instanceof OutboxUnavailableError
      ? 'unavailable'
      : null,
  ]);
  const failedToOpen =
    book.failure instanceof OutboxSupersededError || book.failure instanceof OutboxUnavailableError;

  if (book.loading) {
    return (
      <main className="sb sb--message">
        <h1 className="sb__messageTitle">Opening the book</h1>
        <p className="sb__messageBody">Your plays are on this phone. One moment.</p>
      </main>
    );
  }

  if (failedToOpen && stop === 'superseded' && book.failure instanceof OutboxSupersededError) {
    return (
      <main className="sb sb--message">
        {/* The notice's title is an h2, and this route sits outside the shell. */}
        <h1 className="sb__srOnly">Live scorebook</h1>
        <SupersededNotice
          diagnostic={diagnosticFor(book.failure, SURFACE)}
          outcomes={[]}
          uncertain={book.failure.storeMayBeGone}
          bookOnScreen={false}
        />
      </main>
    );
  }

  if (failedToOpen && stop === 'unavailable') {
    // No "Try again": the refusal is sticky until the page reloads. The reload
    // goes through the platform adapter, as the superseded notice's does.
    return (
      <main className="sb sb--message">
        <h1 className="sb__messageTitle">{UNAVAILABLE_TITLE}</h1>
        <p className="sb__messageBody">
          {lostBody([], false)}{' '}
          {supportCodeLine(
            diagnosticFor(
              book.failure instanceof OutboxUnavailableError
                ? book.failure
                : new OutboxUnavailableError(),
              SURFACE,
            ),
          )}
        </p>
        <button
          className="sb__messageAction"
          onClick={() => {
            navigation.restart();
          }}
          type="button"
        >
          Reload this page
        </button>
      </main>
    );
  }

  if (book.failure !== null) {
    return (
      <main className="sb sb--message">
        <h1 className="sb__messageTitle">The book on this phone could not be opened.</h1>
        <p className="sb__messageBody">
          Nothing has been changed. Try again, or close the app and open it. If it keeps happening,
          this browser may be blocking site data for Good Season.{' '}
          {supportCodeLine(diagnosticFor(book.failure, SURFACE))}
        </p>
        <button className="sb__messageAction" onClick={book.retryOpen} type="button">
          Try again
        </button>
      </main>
    );
  }

  /*
   * Somebody else's book (#43). Nothing of it is shown: the roster is
   * children's names and numbers, and a play recorded here would be sent
   * under whoever is signed in now. Plays it already holds are never deleted,
   * and send under the current session like any other (ADR-0090).
   */
  if (book.refused) {
    return (
      <main className="sb sb--message">
        <h1 className="sb__messageTitle">This book belongs to another sign-in.</h1>
        <p className="sb__messageBody">
          It was set up on this phone by someone who is not signed in now. The person who started it
          can sign in to keep scoring.
        </p>
        <Link className="sb__messageAction" to="/signin">
          Sign in
        </Link>
      </main>
    );
  }

  if (book.game === null) {
    return (
      <main className="sb sb--message">
        <h1 className="sb__messageTitle">This game is not on this phone yet.</h1>
        <p className="sb__messageBody">
          Open it once while you have signal and it will work at the gym without any.
        </p>
      </main>
    );
  }

  const { game, clock, scores, floor } = book;
  const roster = game.roster.filter((player) => game.setup.presentPlayerIds.includes(player.id));
  const onFloor = roster.filter((player) => floor.includes(player.id));
  const bench = roster.filter((player) => !floor.includes(player.id));

  const standingOf = (playerId: string) =>
    foulStanding(scores.byPlayer.get(playerId)?.fouls ?? 0, game.setup.disqualifyingFouls);
  // BUILD_PLAN 5.3: warn loudly, allow override. The referee's book wins when
  // the two disagree, so nothing below stops a play being recorded for them.
  const fouledOutOnFloor = onFloor.filter((player) => standingOf(player.id) === 'out');

  const timeouts = timeoutsIn(book.log, clock.period);
  /*
   * An action armed from the Full row or from More has a sign of its own: a row
   * in the modifiers' slot, the way the foul strip has one, saying what is armed
   * and offering "no player" and cancel. Whether the Full row is on the page or
   * in More does not decide it, so the row is the same element for as long as an
   * action is armed and a change of level never remounts it under a thumb or
   * drops keyboard focus.
   */
  const armedExtra =
    selection?.kind === 'action'
      ? FULL_ACTIONS.find((candidate) => candidate.key === selection.action.key)
      : undefined;
  /*
   * The bench is in More on the shortest screens (the last thing to give), and
   * a bench player picked there needs the same sign: who is coming on, and a way
   * to take it back, in the same slot.
   */
  const benchInMore = fit.level >= 5;
  const armedBench =
    benchInMore && selection?.kind === 'bench'
      ? roster.find((player) => player.id === selection.playerId)
      : undefined;
  const foulRow = foulId === null ? undefined : book.plays.find((row) => row.id === foulId);
  const foulKind = FOUL_KINDS.find((kind) => kind.type === foulRow?.event.type);
  const settled = () => Date.now() - slotSettledAt.current >= SLOT_SETTLE_MS;

  const teamFouls = teamFoulsIn(book.log, clock.period);
  const bonusOf = (fouls: number) =>
    bonusStanding(fouls, game.setup.teamFoulsForBonus, game.setup.teamFoulsForDoubleBonus);
  const foulsLine = (fouls: number) => {
    const standing = bonusOf(fouls);
    return `${String(fouls)} team fouls${
      standing === 'double' ? ', double bonus' : standing === 'bonus' ? ', bonus' : ''
    }`;
  };

  /*
   * Notices: everything on the page that is a message and not a control of the
   * game (a closed book, a pending Finish, a fouled-out player on the floor, the
   * on-court count, plays with no player). They are variable in number, so no
   * fixed set of levels can budget for them. When they do not fit one line each
   * they collapse into ONE line (the most urgent, and a count) that opens a sheet
   * with all of them and their actions, so the core of the book (score, clock,
   * sync, the court row, the scoring actions, the strip, the tray) is never
   * replaced by a fallback on a phone held upright. Most urgent first: the book
   * is closed (nothing lands until it is reopened), Finish is waiting on you, a
   * player who should be off is on, the floor count is wrong, plays have no
   * player.
   */
  const foulNames = fouledOutOnFloor.map((player) => spokenPlayerLabel(player, roster)).join(', ');
  const countSentence = `${String(onFloor.length)} on the floor, ${String(game.setup.onCourtCount)} expected. ${
    onFloor.length > game.setup.onCourtCount
      ? 'Check the substitutions.'
      : bench.length > 0
        ? benchInMore
          ? 'Open More, tap a bench player, then the open spot.'
          : 'Tap a bench player, then the open spot.'
        : 'Everyone who is here is on the floor.'
  }`;
  /*
   * The stop or unsure notice, when there is one. It is the most urgent notice
   * there is (nothing lands, or the book may be behind disk), so it leads the
   * list, takes the same steps as the rest (the full notice, one row, the
   * "+N more" line) and is said by the same live region. The reload is on every
   * form of it: its own button on the page, and in the sheet.
   */
  const unsure = unsureNotice(facts);
  const stopNotice: {
    readonly title: string;
    readonly body: string;
    readonly short: string;
    readonly reference: string;
  } | null = (() => {
    if (stop === 'superseded' && (superseded !== null || book.status.kind === 'superseded')) {
      const reference =
        superseded !== null
          ? diagnosticFor(superseded, SURFACE)
          : book.status.kind === 'superseded'
            ? book.status.diagnostic
            : '';
      const maybeGone =
        superseded !== null
          ? superseded.storeMayBeGone
          : book.status.kind === 'superseded' && book.status.storeMayBeGone;
      return {
        title: 'A newer version of Good Season opened in another tab.',
        body: noticeBody(noticeOutcomesOf(facts), reference, maybeGone, true, mayBeFinalOf(facts)),
        short: stopShort('takeover', maybeGone, facts),
        reference,
      };
    }
    if (stop === 'unavailable') {
      const reference = diagnosticFor(unavailable ?? new OutboxUnavailableError(), SURFACE);
      return {
        title: UNAVAILABLE_TITLE,
        body: `${lostBody(noticeFactsOf(facts), true, mayBeFinalOf(facts))} ${supportCodeLine(reference)}`,
        short: stopShort('wipe', true, facts),
        reference,
      };
    }
    const shortUnsure = unsureShort(facts);
    if (unsure !== null && facts.stale !== null && shortUnsure !== null) {
      return {
        title: unsure.title,
        body: `${unsure.body} ${supportCodeLine(facts.stale.diagnostic)}`,
        short: shortUnsure,
        reference: facts.stale.diagnostic,
      };
    }
    return null;
  })();
  const notices: readonly {
    readonly key: string;
    readonly brief: string;
    readonly long: string;
    readonly action?: ReactNode;
    /** Said even when it is on the screen the first time the notices are looked at. */
    readonly urgent?: boolean;
    /**
     * What a screen reader is told, when it is not just the brief under the
     * notice's own key. A notice that is about several players says each of them
     * under its own key, so a change to one is not a change to the others.
     */
    readonly said?: readonly { readonly key: string; readonly text: string }[];
  }[] = [
    ...(stopNotice === null
      ? []
      : [
          {
            key: 'stop',
            brief: `${stopNotice.short} ${stopNotice.reference}`,
            long: `${stopNotice.title} ${stopNotice.body}`,
            urgent: true,
            // The whole text is said, as the alert it replaces said it, and
            // again when it changes (a fact added, a stop replaced by a wipe).
            said: [{ key: 'stop', text: `${stopNotice.title} ${stopNotice.body}` }],
            action: (
              <button
                className="sb-more__button"
                onClick={() => {
                  navigation.restart();
                }}
                type="button"
              >
                Reload this page
              </button>
            ),
          },
        ]),
    ...(book.finished
      ? [
          {
            key: 'closed',
            brief: 'Closed. Plays wait here.',
            long: 'This book is closed. Anything recorded now waits on this phone until it is reopened.',
            action: (
              <Link
                className="sb-more__button"
                to={{ pathname: `/scorebook/${gameId}/boxscore`, search }}
                state={{ from: 'book' } satisfies BoxScoreLocationState}
              >
                View the box score
              </Link>
            ),
          },
        ]
      : []),
    ...(waiting
      ? [
          {
            key: 'waiting',
            brief: 'Finishing when the plays send.',
            long: 'Finishing as soon as the last plays send. Keep this screen open.',
            action: (
              <button
                className="sb-more__button"
                onClick={() => {
                  setWaiting(false);
                  setNoticesOpen(false);
                }}
                type="button"
              >
                Stop waiting
              </button>
            ),
          },
        ]
      : []),
    ...(fouledOutOnFloor.length > 0
      ? [
          {
            key: 'fouled',
            brief: `${foulNames} fouled out`,
            said: fouledOutOnFloor.map((player) => ({
              key: `fouled:${player.id}`,
              text: `${spokenPlayerLabel(player, roster)} fouled out`,
            })),
            long: `${foulNames} ${fouledOutOnFloor.length === 1 ? 'has' : 'have'} fouled out and ${
              fouledOutOnFloor.length === 1 ? 'is' : 'are'
            } still on the floor. Sub them off, or carry on if the referee's book says otherwise.`,
          },
        ]
      : []),
    ...(onFloor.length !== game.setup.onCourtCount
      ? [
          {
            key: 'count',
            brief: `${String(onFloor.length)} on the floor, ${String(game.setup.onCourtCount)} expected`,
            long: countSentence,
          },
        ]
      : []),
    ...(scores.unattributed > 0
      ? [
          {
            key: 'badge',
            brief: `${String(scores.unattributed)} with no player yet`,
            long: `${String(scores.unattributed)} play${scores.unattributed === 1 ? '' : 's'} with no player yet`,
          },
        ]
      : []),
  ];
  const collapsed = fit.level >= 4 && notices.length >= 2;

  const actions: readonly QuickAction[] = [
    { key: 'FG2', label: '+2', made: 'FG2_MADE', missed: 'FG2_MISS', points: 2 },
    // ABSENT, not disabled, when the league has no three point line
    // (BUILD_PLAN 5.2). A dimmed button a coach cannot use is a question they
    // have to answer every possession.
    ...(game.setup.threePointLine
      ? [{ key: 'FG3', label: '+3', made: 'FG3_MADE', missed: 'FG3_MISS', points: 3 }]
      : []),
    { key: 'FT', label: 'FT', made: 'FT_MADE', missed: 'FT_MISS', points: 1 },
    FOUL,
  ];

  return (
    <main className="sb">
      {/*
        Visually hidden, and not decorative. The live book has no room for a
        title (the header is two scores and a clock) and a screen reader landing
        here with no level-one heading has nothing telling it what this screen
        is. Text rather than `aria-label` on the main, so it is a real heading
        in the document outline.
      */}
      <h1 className="sb__srOnly">Live scorebook</h1>
      {/*
        The score, announced (REVIEW_RUBRIC D4). A sighted scorekeeper sees the
        number roll; this is the same confirmation for somebody listening. It
        changes only when the score does, so a running clock re-rendering the
        header says nothing here.
      */}
      <p className="sb__srOnly" role="status">
        Us {scores.ourScore}, them {scores.theirScore}. Us {foulsLine(teamFouls.us)}. Them{' '}
        {foulsLine(teamFouls.them)}. Timeouts this period: us, {timeoutsLine(timeouts.us, true)};
        them, {timeoutsLine(timeouts.them, true)}.
      </p>
      {/*
        Everything above the tray, which is what is measured to fit
        (`useFit`). The tray is outside it and pinned: Undo, More, Plays and
        Finish are never cut off, in any state.
      */}
      <NoticeAnnouncer notices={notices} />
      <div className={`sb__body${fit.level >= 4 ? ' sb__body--tight' : ''}`} ref={fit.ref}>
        {/* In the fallback there is no "+N more" line to fold it into, so it is always shown. */}
        {stopNotice !== null && (!collapsed || fit.fallback) ? (
          <StopNotice
            body={stopNotice.body}
            onOpen={() => setNoticesOpen(true)}
            oneLine={oneLine}
            reference={stopNotice.reference}
            short={stopNotice.short}
            title={stopNotice.title}
          />
        ) : null}
        {fit.fallback ? (
          /*
           * Even the most compact book does not fit this screen (a phone on its
           * side in a browser tab, a very short window). Said, and not clipped:
           * the tray stays, and so does the sync line, because where the plays
           * are is never hidden. The installed app is portrait.
           */
          <section aria-label="Screen too short" className="sb__fallback">
            <h2 className="sb__fallbackTitle">This screen is too short to score on.</h2>
            <p className="sb__fallbackBody">
              {window.innerWidth > window.innerHeight
                ? 'Turn the phone upright, or make the window taller.'
                : 'Make the window taller, or use a bigger screen.'}{' '}
              {stopNotice === null ? 'Every play recorded so far is saved.' : ''}
            </p>
            <button className="sb__fallbackAction" onClick={fit.recheck} type="button">
              Check again
            </button>
            <SyncIndicator
              compact
              onReopen={() => void reopenGame()}
              onRetry={book.retryNow}
              persistence={book.persistence}
              status={book.status}
            />
          </section>
        ) : (
          <>
            <header className="sb__head">
              <p className="sb__score">
                <span className="sb__scoreLabel">Us</span>
                <span className="sb__scoreValue">{scores.ourScore}</span>
                {/*
            This side's fouls this period, and the bonus the OTHER side is in
            because of them: the scoreboard convention. Text, never a light
            alone.
          */}
                <span
                  className={`sb__fouls${bonusOf(teamFouls.us) === 'none' ? '' : ' sb__fouls--bonus'}`}
                >
                  {foulsLine(teamFouls.us)}
                </span>
                <span className="sb__timeouts">{timeoutsLine(timeouts.us)}</span>
              </p>
              <div className="sb__clock">
                <p className="sb__period">Period {clock.period}</p>
                {/*
            Named from its contents, so the name contains the reading a
            sighted person sees (WCAG 2.5.3) and a screen reader browsing the
            header still finds the clock. An `aria-label` of "Set the clock"
            replaced the reading for a round. The hint is text, hidden from
            the eye only.
          */}
                <button
                  className={`sb__time sb__timeButton${clock.remainingMs < 0 ? ' sb__time--over' : ''}`}
                  onClick={() => void setClock()}
                  type="button"
                >
                  {clockLabel(clock.remainingMs)}
                  <span className="sb__srOnly">, tap to set the clock</span>
                </button>
                <div className="sb__clockControls">
                  <button
                    className="sb__clockButton"
                    onClick={() =>
                      void commit({
                        type: clock.running ? 'CLOCK_STOP' : 'CLOCK_START',
                        team: 'us',
                      })
                    }
                    type="button"
                  >
                    {clock.running ? 'Stop clock' : 'Start clock'}
                  </button>
                  <button
                    className="sb__clockButton"
                    onClick={() => void endPeriod()}
                    type="button"
                  >
                    End period
                  </button>
                </div>
              </div>
              <p className="sb__score">
                <span className="sb__scoreLabel">Them</span>
                <span className="sb__scoreValue">{scores.theirScore}</span>
                <span
                  className={`sb__fouls${bonusOf(teamFouls.them) === 'none' ? '' : ' sb__fouls--bonus'}`}
                >
                  {foulsLine(teamFouls.them)}
                </span>
                <span className="sb__timeouts">{timeoutsLine(timeouts.them)}</span>
              </p>
            </header>

            {!collapsed && fouledOutOnFloor.length > 0 ? (
              <p className="sb__warn">
                {fouledOutOnFloor.map((player) => spokenPlayerLabel(player, roster)).join(', ')}{' '}
                {oneLine
                  ? // One line when nothing else gave enough room. The no-player badge
                    // shares it rather than taking a line of its own: the card says OUT,
                    // and the banner only ever names players still on the floor.
                    `fouled out${
                      scores.unattributed > 0
                        ? `. ${String(scores.unattributed)} with no player yet.`
                        : ', still on the floor.'
                    }`
                  : `${fouledOutOnFloor.length === 1 ? 'has' : 'have'} fouled out and ${
                      fouledOutOnFloor.length === 1 ? 'is' : 'are'
                    } still on the floor. Sub them off, or carry on if the referee's book says otherwise.`}
              </p>
            ) : null}

            <SyncIndicator
              onReopen={() => void reopenGame()}
              compact={compact}
              onRetry={book.retryNow}
              persistence={book.persistence}
              status={book.status}
            />

            {collapsed ? (
              <button
                /*
                 * The name STARTS with what is on the button (WCAG 2.5.3), and goes
                 * on to say what it does.
                 */
                aria-label={`${notices[0]?.brief ?? ''} +${String(notices.length - 1)} more. Open all ${String(notices.length)} notices.`}
                className="sb__notices"
                onClick={() => setNoticesOpen(true)}
                type="button"
              >
                <span className="sb__noticesTop">{notices[0]?.brief}</span>{' '}
                <span className="sb__noticesCount">+{notices.length - 1} more</span>
              </button>
            ) : null}

            {!collapsed && waiting ? (
              <p className="sb__waiting">
                <span>
                  {oneLine
                    ? 'Finishing when the plays send.'
                    : 'Finishing as soon as the last plays send. Keep this screen open.'}
                </span>
                <button
                  className="sb__waitingCancel"
                  onClick={() => setWaiting(false)}
                  type="button"
                >
                  Stop waiting
                </button>
              </p>
            ) : null}

            {!collapsed && book.finished ? (
              // The controls stay live on purpose: a play recorded after the whistle
              // is kept on this phone and the sync indicator offers the reopen that
              // lets it land (ADR-0047). Said plainly, rather than claiming a block
              // that is not there.
              <p className={`sb__final${oneLine ? ' sb__final--one' : ''}`}>
                <span>
                  {oneLine
                    ? 'Closed. Plays wait here.'
                    : 'This book is closed. Anything recorded now waits on this phone until it is reopened.'}
                </span>
                <Link
                  className="sb__boxScoreLink"
                  to={{ pathname: `/scorebook/${gameId}/boxscore`, search }}
                  state={{ from: 'book' } satisfies BoxScoreLocationState}
                >
                  {oneLine ? 'Box score' : 'View the box score'}
                </Link>
              </p>
            ) : null}

            {/*
        BUILD_PLAN 5.3: the on-court count is a warning, never a block. A
        scorekeeper who cannot record a basket because the app thinks six are
        on the floor abandons the app, so this sits above the court and every
        button below it keeps working.
      */}
            {!collapsed && onFloor.length !== game.setup.onCourtCount ? (
              <p className="sb__warn">{countSentence}</p>
            ) : null}

            {!collapsed && scores.unattributed > 0 && !(oneLine && fouledOutOnFloor.length > 0) ? (
              <p className="sb__badge">
                {oneLine
                  ? `${String(scores.unattributed)} with no player yet`
                  : `${String(scores.unattributed)} play${scores.unattributed === 1 ? '' : 's'} with no player yet`}
              </p>
            ) : null}

            <section aria-label="On court" className="sb__court">
              {onFloor.length === 0 && selection?.kind !== 'bench' ? (
                <p className="sb__empty">
                  Nobody is on the floor. Tap a player below, then tap where they go.
                </p>
              ) : (
                onFloor.map((player) => {
                  const line = scores.byPlayer.get(player.id);
                  const armed = selection?.kind === 'player' && selection.playerId === player.id;
                  // BUILD_PLAN 5.4's `f:3!`: one away reads loud, out reads louder,
                  // in words as well as colour.
                  const standing = standingOf(player.id);
                  return (
                    <button
                      aria-pressed={armed}
                      className={`sb__player${armed ? ' sb__player--armed' : ''}${
                        standing === 'out' ? ' sb__player--out' : ''
                      }`}
                      key={player.id}
                      onClick={() => tapPlayer(player.id, true)}
                      type="button"
                    >
                      <span className="sb__jersey">{player.jerseyNumber ?? '-'}</span>
                      <span className="sb__name">{playerLabel(player, roster)}</span>
                      {/*
                  What the eye reads is short, because a card is 64px wide: points
                  and fouls on one line, and a second line that is ALWAYS there
                  (empty until it has something to say) so a card never changes
                  height when a player gets in foul trouble and moves the book. The
                  words a screen reader gets are the long form.
                */}
                      <span className={`sb__line${standing === 'ok' ? '' : ' sb__line--trouble'}`}>
                        <span aria-hidden="true" className="sb__lineStat">
                          {line?.points ?? 0}p {line?.fouls ?? 0}f
                        </span>
                        <span aria-hidden="true" className="sb__lineNote">
                          {standing === 'out' ? 'OUT' : standing === 'trouble' ? '1 left' : ''}
                        </span>
                        <span className="sb__srOnly">
                          {line?.points ?? 0} pts, {line?.fouls ?? 0} fouls
                          {standing === 'out'
                            ? ', out'
                            : standing === 'trouble'
                              ? ', one left'
                              : ''}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
              {/*
          Open spots, shown only while a bench player is armed and the floor
          is short, so the control exists exactly when it can do something.
        */}
              {selection?.kind === 'bench'
                ? Array.from({ length: Math.max(0, game.setup.onCourtCount - onFloor.length) }).map(
                    (_, index) => (
                      <button
                        aria-label="Open spot on the floor"
                        className="sb__player sb__player--open"
                        key={`open-${String(index)}`}
                        onClick={() => fillSpot(selection.playerId)}
                        type="button"
                      >
                        <span aria-hidden="true" className="sb__name">
                          Open spot
                        </span>
                      </button>
                    ),
                  )
                : null}
            </section>

            {benchInMore ? null : (
              <section aria-label="Bench" className="sb__bench">
                {bench.length === 0 ? (
                  <p className="sb__empty">Everyone here is on the floor.</p>
                ) : (
                  bench.map((player) => {
                    const armed = selection?.kind === 'bench' && selection.playerId === player.id;
                    return (
                      <button
                        aria-pressed={armed}
                        className={`sb__benchPlayer${armed ? ' sb__benchPlayer--armed' : ''}`}
                        key={player.id}
                        onClick={() => tapPlayer(player.id, false)}
                        type="button"
                      >
                        <span className="sb__jersey">{player.jerseyNumber ?? '-'}</span>
                        <span className="sb__name">{playerLabel(player, roster)}</span>
                      </button>
                    );
                  })
                )}
              </section>
            )}

            {/* The modifiers (or the strip, or the armed row), the scoring actions and the
                Full row. On a wide screen the first two share one row (`.sb__pad`). */}
            <div className="sb__pad">
              {foulRow !== undefined && foulKind !== undefined ? (
                /*
                 * The foul-kind strip, in the slot the three modifiers hold. A row of
                 * the same height in the same place, so showing or hiding it moves no
                 * other control and takes nothing from the player cards (BUILD_PLAN
                 * 5.4: large targets, no scrolling during play). The modifiers are
                 * back when it goes. The foul is already on the book as a personal
                 * foul; this changes it. Words on every button and the current kind
                 * pressed, so colour carries nothing alone. Keyboard focus inside holds it
                 * open; the timeline's Change kind is the path with no timer.
                 */
                <section
                  aria-label="Foul kind"
                  className="sb__foulType"
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) startFoulFuse();
                  }}
                  onFocus={(event) => {
                    // Keyboard focus holds the strip; pointer or touch focus never does.
                    if (event.target instanceof HTMLElement && focusVisible(event.target))
                      stopFoulFuse();
                    else startFoulFuse();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') closeStrip();
                  }}
                  ref={stripRef}
                >
                  <p className="sb__srOnly" role="status">
                    Foul recorded as {foulKind.label.toLowerCase()}. Change the kind with these
                    buttons.
                  </p>
                  {FOUL_KINDS.map((kind) => {
                    const current = kind.type === foulRow.event.type;
                    return (
                      <button
                        aria-pressed={current}
                        className={`sb__modifier sb__foulKind${current ? ' sb__modifier--on' : ''}`}
                        key={kind.type}
                        onClick={() => void changeFoul(foulRow, kind.type)}
                        type="button"
                      >
                        {kind.label}
                      </button>
                    );
                  })}
                </section>
              ) : armedExtra !== undefined || armedBench !== undefined ? (
                <section
                  aria-label={armedBench === undefined ? 'Armed play' : 'Armed substitution'}
                  className="sb__modifiers"
                  key="armed-play"
                >
                  <p className="sb__armedNote" role="status">
                    {armedExtra !== undefined
                      ? `${armedExtra.label}${them ? ' for them' : ''}: tap a player`
                      : `${armedBench === undefined ? '' : playerLabel(armedBench, roster)} on: tap ${
                          onFloor.length < game.setup.onCourtCount
                            ? 'the open spot'
                            : 'who comes off'
                        }`}
                  </p>
                  {armedExtra === undefined ? null : (
                    <button
                      className="sb__modifier"
                      onClick={() => {
                        holdModifiers();
                        play(armedExtra, null);
                      }}
                      type="button"
                    >
                      No player
                    </button>
                  )}
                  <button
                    className="sb__modifier"
                    onClick={() => {
                      holdModifiers();
                      arm(null);
                    }}
                    type="button"
                  >
                    Cancel
                  </button>
                </section>
              ) : (
                <div className="sb__modifiers">
                  <button
                    aria-pressed={missing}
                    className={`sb__modifier${missing ? ' sb__modifier--on' : ''}`}
                    onClick={() => settled() && setMissing((value) => !value)}
                    type="button"
                  >
                    {missing ? 'Recording misses' : 'Recording makes'}
                  </button>
                  <button
                    aria-pressed={them}
                    className={`sb__modifier${them ? ' sb__modifier--on' : ''}`}
                    onClick={() => settled() && setThem((value) => !value)}
                    type="button"
                  >
                    {them ? 'For them' : 'For us'}
                  </button>
                  {/*
          Mid-game and non-destructive (BUILD_PLAN 5.4). It changes which
          buttons are on the screen and nothing else: no event is written, none
          is rewritten, and a play recorded in one mode reads the same in the
          other.
        */}
                  <button
                    aria-pressed={full}
                    className={`sb__modifier${full ? ' sb__modifier--on' : ''}`}
                    onClick={() => {
                      if (!settled()) return;
                      arm(null);
                      setFull((value) => !value);
                    }}
                    type="button"
                  >
                    {full ? 'Full mode' : 'Quick mode'}
                  </button>
                </div>
              )}

              <section aria-label="Actions" className="sb__actions">
                {actions.map(actionButton)}
              </section>

              {full && !short ? (
                <section aria-label="More actions" className="sb__actions sb__actions--full">
                  {FULL_ACTIONS.map(actionButton)}
                </section>
              ) : null}
            </div>
          </>
        )}
      </div>

      <div className="sb__tray">
        <button
          /*
           * A STABLE accessible name, for the reason the action buttons carry
           * one: without it this control is called "Plays" until an
           * unattributed play appears and "Plays 3" afterwards. The count is
           * already in the accessibility tree as the badge above, so here it is
           * reinforcement for the eye and nothing is lost by hiding it.
           */
          aria-label="Plays"
          className="sb__trayButton"
          onClick={() => setTimeline(true)}
          type="button"
        >
          <span aria-hidden="true">Plays</span>
          {scores.unattributed > 0 ? (
            <span aria-hidden="true" className="sb__trayCount">
              {scores.unattributed}
            </span>
          ) : null}
        </button>
        <button className="sb__trayButton" onClick={() => setMore(true)} type="button">
          More
        </button>
        <button
          className="sb__undo sb__trayButton"
          disabled={!book.canUndo}
          onClick={() => void book.undo().catch(refused)}
          type="button"
        >
          Undo last play
        </button>
        <button
          className="sb__trayButton"
          onClick={() => void (book.finished ? reopenGame() : finishGame())}
          type="button"
        >
          {book.finished ? 'Reopen game' : 'Finish game'}
        </button>
      </div>

      <Dialog
        onClosed={() => {
          const next = afterMore.current;
          afterMore.current = null;
          next?.();
        }}
        onOpenChange={setMore}
        open={more}
        size="md"
        title="More"
      >
        <div className="sb-more">
          {!book.finished && !waiting ? (
            <Link
              className="sb-more__button sb-more__link"
              to={{ pathname: `/scorebook/${gameId}/boxscore`, search }}
              state={{ from: 'book' } satisfies BoxScoreLocationState}
            >
              View the box score
            </Link>
          ) : null}
          <section aria-label="Timeouts" className="sb-more__group">
            <h3 className="sb-more__heading">
              Timeouts this period: us, {timeoutsLine(timeouts.us, true)}; them,{' '}
              {timeoutsLine(timeouts.them, true)}
            </h3>
            {(
              [
                ['us', 'TIMEOUT_FULL', 'Us: full timeout'],
                ['us', 'TIMEOUT_SHORT', 'Us: short timeout'],
                ['them', 'TIMEOUT_FULL', 'Them: full timeout'],
                ['them', 'TIMEOUT_SHORT', 'Them: short timeout'],
              ] as const
            ).map(([team, type, label]) => (
              <button
                className="sb-more__button"
                key={label}
                onClick={() => void recordFromMore({ type, team })}
                type="button"
              >
                {label}
              </button>
            ))}
          </section>
          <section aria-label="Jump ball" className="sb-more__group">
            <h3 className="sb-more__heading">Jump ball</h3>
            <button
              className="sb-more__button"
              onClick={() => void recordFromMore({ type: 'JUMP_BALL', team: 'us' })}
              type="button"
            >
              Possession to us
            </button>
            <button
              className="sb-more__button"
              onClick={() => void recordFromMore({ type: 'JUMP_BALL', team: 'them' })}
              type="button"
            >
              Possession to them
            </button>
          </section>
          {full && short ? (
            /*
             * Full mode's extra actions, on a screen with no room for their row.
             * Tapping one arms it exactly as the row's button does (both orders,
             * the four second fuse) and closes the sheet so the players are
             * there to tap.
             */
            <section aria-label="More plays" className="sb-more__group">
              <h3 className="sb-more__heading">More plays: tap one, then the player</h3>
              {FULL_ACTIONS.map((action) => (
                <button
                  className="sb-more__button"
                  key={action.key}
                  onClick={() => {
                    setMore(false);
                    tapAction(action);
                  }}
                  type="button"
                >
                  {action.label}
                </button>
              ))}
            </section>
          ) : null}
          {compact && book.persistence === 'denied' ? (
            <p className="sb-sync__persist">
              This browser has not promised to keep the book. Install Good Season to make it
              permanent.
            </p>
          ) : null}
          {benchInMore ? (
            /*
             * The bench, on the shortest screens, which have no room for its row.
             * Picking a player here is what tapping them on the bench is: they are
             * armed, the sheet closes, and the next tap on a court player is the
             * swap (or an open spot is the entry).
             */
            <section aria-label="Bench" className="sb-more__group">
              <h3 className="sb-more__heading">
                Bench: tap one, then{' '}
                {onFloor.length < game.setup.onCourtCount ? 'the open spot' : 'who comes off'}
              </h3>
              {bench.length === 0 ? (
                <p className="sb__empty">Everyone here is on the floor.</p>
              ) : (
                bench.map((player) => (
                  <button
                    className="sb-more__button"
                    key={player.id}
                    onClick={() => {
                      setMore(false);
                      tapPlayer(player.id, false);
                    }}
                    type="button"
                  >
                    {spokenPlayerLabel(player, roster)}
                  </button>
                ))
              )}
            </section>
          ) : null}
          <section aria-label="Note" className="sb-more__group">
            <h3 className="sb-more__heading">Note</h3>
            <button className="sb-more__button" onClick={() => void addNote()} type="button">
              Add a note
            </button>
          </section>
        </div>
      </Dialog>

      <Dialog onOpenChange={setNoticesOpen} open={noticesOpen} size="md" title="Notices">
        <ul className="sb-notices">
          {notices.map((notice) => (
            <li className="sb-notices__item" key={notice.key}>
              <p className="sb-notices__text">{notice.long}</p>
              {notice.action}
            </li>
          ))}
        </ul>
      </Dialog>

      <PlaysTimeline
        onAttribute={(row, playerId) => void book.attribute(row, playerId).catch(refused)}
        onClose={() => setTimeline(false)}
        onRetype={retypeWithFeedback}
        onVoid={(row) => void voidFromTimeline(row)}
        open={timeline}
        plays={book.plays}
        roster={roster}
      />
    </main>
  );
}

/** A dialog over the live book, so unsent plays may be on the screen. */
/**
 * A write whose first attempt may have committed (`OutboxUncertainError`).
 *
 * The screen did not take it, so the book on screen can be behind disk: the
 * reload comes first, then the check, then the entry if it is missing. Never
 * "Nothing was changed" and never a bare "Try it again", because the re-tap
 * that invites is a second play, an Undo of the Undo, or a second swap.
 */
function uncertainCopy(mayBeFinal: boolean): { readonly title: string; readonly body: string } {
  return {
    title: 'That may or may not have been saved.',
    // Same rule as `unsureNotice`: no copy invites entering a play into a game
    // a Finish has closed or may have closed.
    body: `The timeline and the score now show what this phone holds. Check them${
      mayBeFinal ? '.' : ' before you enter it again.'
    }`,
  };
}

/**
 * The book is closed, with the score the SERVER holds. A second Finish of a
 * final game is answered 200 with the stored row and keeps the first committed
 * score, so what was just typed can differ, and when it does the screen says
 * so with both. An answer whose game carries no well-formed score (a server
 * not keeping the contract) claims neither.
 */
function closedCopy(
  entered: FinalScore,
  stored: FinalScore | null | undefined,
): { readonly title: string; readonly body: string } {
  const title = 'The book is closed.';
  if (stored === null || stored === undefined) {
    return {
      title,
      body: 'The server did not say which final score it holds, so it may not be the one entered here. Reopen it if something needs changing.',
    };
  }
  const final = `Final: us ${String(stored.us)}, them ${String(stored.them)}.`;
  if (stored.us === entered.us && stored.them === entered.them) {
    return { title, body: `${final} Reopen it if something needs changing.` };
  }
  return {
    title,
    body: `${final} This game was already finished with that score, and it stands. The us ${String(entered.us)}, them ${String(entered.them)} entered now was not saved. Reopen it if something needs changing.`,
  };
}

function unavailableCopy(
  facts: readonly LostFact[],
  finalOnServer: boolean,
): {
  readonly title: string;
  readonly body: string;
} {
  return { title: UNAVAILABLE_TITLE, body: lostBody(facts, true, finalOnServer) };
}

/**
 * One polite live region, present at every level, that says each notice when it
 * is NEW or when what it says has CHANGED.
 *
 * Which notices are on the page changes with the screen (a notice that is
 * collapsed is only a count on a button), so what a screen reader hears cannot
 * ride on the notices' own markup. It is keyed on the notices themselves: a
 * notice that was there before is not said again when the layout changes, and
 * the ones present when the book opens are not read out as news. A key alone
 * is not enough: "4 on the floor, 5 expected" becoming "3 on the floor, 5
 * expected" is the same notice with news in it. And a notice about several
 * players says each under its own key (`said`), so one of two fouled-out players
 * being subbed off does not re-announce the other.
 */
function NoticeAnnouncer({
  notices,
}: {
  readonly notices: readonly {
    readonly key: string;
    readonly brief: string;
    readonly urgent?: boolean;
    readonly said?: readonly { readonly key: string; readonly text: string }[];
  }[];
}) {
  const seen = useRef<ReadonlyMap<string, string> | null>(null);
  const [said, setSaid] = useState({ text: '', count: 0 });

  useEffect(() => {
    const items = notices.flatMap(
      (notice) => notice.said ?? [{ key: notice.key, text: notice.brief }],
    );
    const now = new Map(items.map((item) => [item.key, item.text]));
    const before = seen.current;
    seen.current = now;
    /*
     * The notices already there when the book opens are not news, except an
     * urgent one (a stop): this region replaced the alert such a notice used to
     * carry, which was said whenever it appeared.
     */
    const urgent = new Set(
      notices
        .filter((notice) => notice.urgent === true)
        .flatMap((notice) => notice.said?.map((item) => item.key) ?? [notice.key]),
    );
    const fresh = items.filter(
      (item) => before?.get(item.key) !== item.text && (before !== null || urgent.has(item.key)),
    );
    if (fresh.length === 0) return;
    setSaid((previous) => ({
      text: fresh.map((item) => item.text).join('. '),
      count: previous.count + 1,
    }));
  }, [notices]);

  return (
    <p className="sb__srOnly" role="status">
      <span key={said.count}>{said.text}</span>
    </p>
  );
}
